import React, { useState, useCallback, useEffect } from 'react';
import { PowerCostInput } from './calculator/PowerCostInput';
import { CoinSelector } from './calculator/CoinSelector';
import { MinerTable } from './calculator/MinerTable';
import { useMiners } from '../hooks/useMiners';
import { useMinerstat } from '../hooks/useMinerstat';
import { useCryptoPrices } from '../hooks/useCryptoPrices';
import { calculateProfitability } from '../utils/calculations';
import { ExchangeRates } from '../utils/currencyUtils';
import { Loader } from './common/Loader';
import { refreshAllData } from '../utils/api/index';
import { RefreshCw, AlertTriangle, Filter } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { getAlgorithms, getSupportedCoins } from '../utils/miners';

interface CalculatorProps {
  currency: string;
  exchangeRates: ExchangeRates;
  powerCost: number;
  onPowerCostChange: (cost: number) => void;
  onCurrencyChange: (currency: string) => void;
}

export function Calculator({ 
  currency, 
  exchangeRates, 
  powerCost, 
  onPowerCostChange, 
  onCurrencyChange 
}: CalculatorProps) {
  const { miners: localMiners, isLoading: minersLoading, error: minersError, refresh } = useMiners();
  const { miners: minerstatMiners, isLoading: minerstatLoading, error: minerstatError } = useMinerstat();
  const { prices, isLoading: pricesLoading, error: pricesError, refresh: refreshPrices, lastUpdate } = useCryptoPrices();
  const [selectedCoin, setSelectedCoin] = useState<string | null>(null);
  const [selectedAlgorithm, setSelectedAlgorithm] = useState<string | null>(null);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [nextRefresh, setNextRefresh] = useState(10 * 60 * 1000); // 10 minutes

  // Combine miners from both sources
  const allMiners = [...localMiners, ...minerstatMiners];

  // Update countdown timer
  useEffect(() => {
    const timer = setInterval(() => {
      setNextRefresh(prev => {
        if (prev <= 1000) {
          refreshPrices(false);
          return 10 * 60 * 1000; // Reset to 10 minutes
        }
        return prev - 1000;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [refreshPrices]);

  const handleRefresh = useCallback(async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    try {
      await Promise.all([
        refreshAllData(),
        refresh(),
        refreshPrices(true)
      ]);
      setNextRefresh(10 * 60 * 1000); // Reset to 10 minutes
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  }, [refresh, refreshPrices, isRefreshing]);

  if (minersLoading || pricesLoading || minerstatLoading) {
    return <Loader />;
  }

  const minerProfits = allMiners.map(miner => {
    const profitability = calculateProfitability(miner, prices, powerCost, currency, exchangeRates);
    return {
      ...miner,
      ...profitability
    };
  });

  const filteredMiners = minerProfits.filter(miner => {
    if (selectedCoin && selectedAlgorithm) {
      return (miner.coin === selectedCoin || miner.supportedCoins?.includes(selectedCoin)) 
        && miner.algorithm === selectedAlgorithm;
    }
    if (selectedCoin) {
      return miner.coin === selectedCoin || miner.supportedCoins?.includes(selectedCoin);
    }
    if (selectedAlgorithm) {
      return miner.algorithm === selectedAlgorithm;
    }
    return true;
  });

  const algorithms = getAlgorithms();
  const supportedCoins = getSupportedCoins();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Mining Calculator</h1>
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-500">
              Next refresh in {Math.ceil(nextRefresh / 1000 / 60)}m
            </div>
            <button
              onClick={handleRefresh}
              disabled={isRefreshing}
              className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors ${
                isRefreshing ? 'opacity-50 cursor-not-allowed' : ''
              }`}
            >
              <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              {isRefreshing ? 'Refreshing...' : 'Refresh Data'}
            </button>
          </div>
        </div>

        {(minersError || pricesError || minerstatError) && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
            <p className="text-sm text-amber-700">{minersError || pricesError || minerstatError}</p>
          </div>
        )}

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <PowerCostInput 
            powerCost={powerCost}
            currency={currency}
            onPowerCostChange={onPowerCostChange}
            onCurrencyChange={onCurrencyChange}
          />
        </div>

        <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Filter className="w-5 h-5 text-gray-500" />
              Filters
            </h2>
            <div className="flex gap-4">
              <select
                value={selectedAlgorithm || ''}
                onChange={(e) => setSelectedAlgorithm(e.target.value || null)}
                className="rounded-lg border-gray-300"
              >
                <option value="">All Algorithms</option>
                {algorithms.map(algo => (
                  <option key={algo} value={algo}>{algo}</option>
                ))}
              </select>
              <select
                value={selectedCoin || ''}
                onChange={(e) => setSelectedCoin(e.target.value || null)}
                className="rounded-lg border-gray-300"
              >
                <option value="">All Coins</option>
                {supportedCoins.map(coin => (
                  <option key={coin} value={coin}>{coin}</option>
                ))}
              </select>
            </div>
          </div>
          <CoinSelector
            selectedCoin={selectedCoin}
            onCoinSelect={setSelectedCoin}
            marketData={prices}
            isLoading={pricesLoading}
            currency={currency}
            exchangeRates={exchangeRates}
          />
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100">
          <MinerTable
            miners={filteredMiners}
            isLoading={false}
            sortField="profit24"
            sortDirection="desc"
            onSort={() => {}}
            currency={currency}
            exchangeRates={exchangeRates}
          />
        </div>
      </div>
    </div>
  );
}